/**
 * AktenErweiterterFilter — 7 neue Filter-Kriterien für die Akten-Übersicht
 * (Issue #653 — Stufe 2 C: Filter-Bar + URL-Param-Persistenz + localStorage)
 *
 * Scope: reine Logik-Funktionen (kein React). Kein Supabase-Import hier.
 */

// ─── Typ ──────────────────────────────────────────────────────────────────────

export interface AktenErweiterterFilter {
  bezahl_status?: string[];      // 'offen' | 'teilweise_bezahlt' | 'bezahlt' | 'abgeschrieben'
  forderungs_stufe?: number[];   // 0–9 (forderung_stage auf akten)
  forderung_offen?: boolean;     // Toggle: nur Akten mit offener Forderung
  alter_bucket?: string[];       // 'lt14' | '14_30' | '30_60' | '60_90' | 'gt90'
  faellig_seit?: string[];       // 'nicht_faellig' | 'd0_7' | 'd7_30' | 'd30_90' | 'gt90d'
  mit_frist?: string[];          // 'aktiv' | 'ueberfaellig' | 'keine'
  betrag_min?: number;           // rechnung_betrag >= betrag_min
  betrag_max?: number;           // rechnung_betrag <= betrag_max
  created_via?: string[];        // 'manuell' | 'winvalue_sync' | 'kiosk' | 'mcp' | 'email_import' | 'api' | 'csv_import'
}

// ─── Konstanten ────────────────────────────────────────────────────────────────

export const DEFAULT_BETRAG_MAX = 10000;

export const BEZAHL_STATUS_OPTIONS = [
  { value: 'offen',            label: 'Offen' },
  { value: 'teilweise_bezahlt',label: 'Teilweise bezahlt' },
  { value: 'bezahlt',          label: 'Bezahlt' },
  { value: 'abgeschrieben',    label: 'Abgeschrieben' },
] as const;

export const ALTER_BUCKET_OPTIONS = [
  { value: 'lt14',  label: '< 14 Tage' },
  { value: '14_30', label: '14–30 Tage' },
  { value: '30_60', label: '30–60 Tage' },
  { value: '60_90', label: '60–90 Tage' },
  { value: 'gt90',  label: '> 90 Tage' },
] as const;

export const FAELLIG_SEIT_OPTIONS = [
  { value: 'nicht_faellig', label: 'Nicht fällig' },
  { value: 'd0_7',          label: 'Fällig 0–7 Tage' },
  { value: 'd7_30',         label: 'Fällig 7–30 Tage' },
  { value: 'd30_90',        label: 'Fällig 30–90 Tage' },
  { value: 'gt90d',         label: '> 90 Tage fällig' },
] as const;

export const MIT_FRIST_OPTIONS = [
  { value: 'aktiv',       label: 'Hat aktive Frist' },
  { value: 'ueberfaellig',label: 'Frist überfällig' },
  { value: 'keine',       label: 'Keine Frist' },
] as const;

export const CREATED_VIA_OPTIONS = [
  { value: 'manuell',       label: 'Manuell' },
  { value: 'winvalue_sync', label: 'WinValue' },
  { value: 'kiosk',         label: 'Kiosk' },
  { value: 'mcp',           label: 'KI-Agent' },
  { value: 'email_import',  label: 'E-Mail' },
  { value: 'api',           label: 'API' },
  { value: 'csv_import',    label: 'CSV' },
] as const;

// ─── URL-Param-Serialisierung ─────────────────────────────────────────────────

/** Wandelt den erweiterten Filter in URLSearchParams um (für shareable Links). */
export function erweiterterFilterToUrlParams(filter: AktenErweiterterFilter): URLSearchParams {
  const p = new URLSearchParams();

  if (filter.bezahl_status?.length)      p.set('bezahl', filter.bezahl_status.join(','));
  if (filter.forderungs_stufe?.length)   p.set('stufe', filter.forderungs_stufe.join(','));
  if (filter.forderung_offen)            p.set('foffen', '1');
  if (filter.alter_bucket?.length)       p.set('alter', filter.alter_bucket.join(','));
  if (filter.faellig_seit?.length)       p.set('faellig', filter.faellig_seit.join(','));
  if (filter.mit_frist?.length)          p.set('frist', filter.mit_frist.join(','));
  if (filter.created_via?.length)        p.set('ersteller', filter.created_via.join(','));

  // Betragsbereich nur schreiben wenn von Default abweichend
  if (typeof filter.betrag_min === 'number' && filter.betrag_min !== 0) {
    p.set('betrag_min', String(filter.betrag_min));
  }
  if (typeof filter.betrag_max === 'number' && filter.betrag_max !== DEFAULT_BETRAG_MAX) {
    p.set('betrag_max', String(filter.betrag_max));
  }

  return p;
}

/** Liest den erweiterten Filter aus URLSearchParams zurück. */
export function erweiterterFilterFromUrlParams(params: URLSearchParams): AktenErweiterterFilter {
  const f: AktenErweiterterFilter = {};

  const bezahl = params.get('bezahl');
  if (bezahl) f.bezahl_status = bezahl.split(',').filter(Boolean);

  const stufe = params.get('stufe');
  if (stufe) f.forderungs_stufe = stufe.split(',').map(Number).filter(n => !isNaN(n));

  if (params.get('foffen') === '1') f.forderung_offen = true;

  const alter = params.get('alter');
  if (alter) f.alter_bucket = alter.split(',').filter(Boolean);

  const faellig = params.get('faellig');
  if (faellig) f.faellig_seit = faellig.split(',').filter(Boolean);

  const frist = params.get('frist');
  if (frist) f.mit_frist = frist.split(',').filter(Boolean);

  const ersteller = params.get('ersteller');
  if (ersteller) f.created_via = ersteller.split(',').filter(Boolean);

  const bMin = params.get('betrag_min');
  const bMax = params.get('betrag_max');
  if (bMin !== null) f.betrag_min = Number(bMin);
  if (bMax !== null) f.betrag_max = Number(bMax);

  return f;
}

// ─── localStorage-Persistenz ──────────────────────────────────────────────────

/** Serialisiert den Filter als JSON-String (für localStorage). */
export function erweiterterFilterToJson(filter: AktenErweiterterFilter): string {
  return JSON.stringify(filter);
}

/** Deserialisiert den Filter aus einem JSON-String (aus localStorage). */
export function erweiterterFilterFromJson(raw: string | null | undefined): AktenErweiterterFilter {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    return parsed as AktenErweiterterFilter;
  } catch {
    return {};
  }
}

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────

/** Gibt true zurück, wenn mindestens ein Filter aktiv ist. */
export function hatErweitertenFilter(filter: AktenErweiterterFilter): boolean {
  if (filter.bezahl_status?.length) return true;
  if (filter.forderungs_stufe?.length) return true;
  if (filter.forderung_offen) return true;
  if (filter.alter_bucket?.length) return true;
  if (filter.faellig_seit?.length) return true;
  if (filter.mit_frist?.length) return true;
  if (typeof filter.betrag_min === 'number' && filter.betrag_min > 0) return true;
  if (typeof filter.betrag_max === 'number' && filter.betrag_max < DEFAULT_BETRAG_MAX) return true;
  if (filter.created_via?.length) return true;
  return false;
}

export interface AlterBucketRange {
  from: string;  // ISO date YYYY-MM-DD (inklusiv)
  to: string;    // ISO date YYYY-MM-DD (inklusiv)
}

/** Berechnet Datums-Ranges für die Alter-Bucket-Filter. */
export function berechneAlterBucketBedingungen(
  buckets: string[] | undefined,
  now: Date = new Date(),
): AlterBucketRange[] | null {
  if (!buckets?.length) return null;

  const toIso = (d: Date) => d.toISOString().split('T')[0];
  const sub = (d: Date, days: number) => {
    const r = new Date(d);
    r.setDate(r.getDate() - days);
    return r;
  };

  const ranges: AlterBucketRange[] = [];
  for (const b of buckets) {
    switch (b) {
      case 'lt14':
        ranges.push({ from: toIso(sub(now, 14)), to: toIso(now) });
        break;
      case '14_30':
        ranges.push({ from: toIso(sub(now, 30)), to: toIso(sub(now, 14)) });
        break;
      case '30_60':
        ranges.push({ from: toIso(sub(now, 60)), to: toIso(sub(now, 30)) });
        break;
      case '60_90':
        ranges.push({ from: toIso(sub(now, 90)), to: toIso(sub(now, 60)) });
        break;
      case 'gt90':
        ranges.push({ from: '1970-01-01', to: toIso(sub(now, 90)) });
        break;
    }
  }
  return ranges.length > 0 ? ranges : null;
}
